import { useEffect, useMemo, useRef, useState } from "react";
import { Group, Loader, NativeSelect, Stack, Text } from "@mantine/core";
import { useServerApi } from "../hooks/userServerApi";
import { useFormatter } from "../hooks/useFomatter";
import EntityTabAnchor from "../pages/account/payroll2/EntityTabAnchor";
import DataTable from "./dataTable";
import {
  buildForecastSearchQuery,
  buildUpcomingMonthOptions,
  formatExpectedPeriod,
  formatForecastProduct,
  getForecastDebitNote,
  getForecastOrder,
} from "../helpers/walletForecastBids";

const PAGE_SIZE = 20;

const WalletForecastBids = ({ form }) => {
  const walletId = form?.values?._id?.toString?.() ?? form?.values?._id;
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [periodFilter, setPeriodFilter] = useState(null);
  const apiRef = useRef(api);
  apiRef.current = api;

  const monthOptions = useMemo(() => buildUpcomingMonthOptions(), []);

  useEffect(() => {
    if (!walletId) {
      setBids([]);
      setTotalDocs(0);
      setCurrentPage(1);
      return;
    }

    let cancelled = false;

    const loadBids = async () => {
      setLoading(true);
      try {
        const result = await apiRef.current.search({
          apiEntity: "bid",
          searchQuery: buildForecastSearchQuery({
            walletId,
            periodFilter,
            searchText,
          }),
          pageSize,
          currentPage,
          sort: { by: "expectedPeriod", order: "asc" },
          byAggregation: false,
        });

        if (cancelled) return;

        const docs = result?.docs ?? [];
        setBids(docs);
        setTotalDocs(result?.total ?? docs.length);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching wallet forecast bids:", error);
          setBids([]);
          setTotalDocs(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadBids();

    return () => {
      cancelled = true;
    };
  }, [walletId, periodFilter, searchText, currentPage, pageSize]);

  const totalAmount = useMemo(
    () =>
      bids.reduce(
        (sum, bid) => sum + (bid.expectedAmount ?? bid.paidAmount ?? 0),
        0,
      ),
    [bids],
  );

  const columns = useMemo(
    () => [
      {
        field: "code",
        headerName: "Bid",
        sortable: true,
        cellRender: (col, bid) => (
          <Text size="xs" weight={600}>
            {bid.code || "-"}
          </Text>
        ),
      },
      {
        field: "type",
        headerName: "Type",
        sortable: true,
      },
      {
        field: "order.code",
        headerName: "Order",
        cellRender: (col, bid) => {
          const order = getForecastOrder(bid);
          const debitNote = getForecastDebitNote(bid);
          return (
            <Stack spacing={0}>
              <EntityTabAnchor
                entity="order"
                id={order?._id || order}
                label={order?.code}
              />
              <EntityTabAnchor
                entity="debitNote"
                id={debitNote?._id || debitNote}
                label={debitNote?.code}
              />
            </Stack>
          );
        },
      },
      {
        field: "product",
        headerName: "Product",
        cellRender: (col, bid) => {
          const product = formatForecastProduct(bid);
          return (
            <Stack spacing={0}>
              <Text size="xs" lineClamp={1}>
                {product.title}
              </Text>
              {product.subtitle ? (
                <Text size="xs" color="dimmed" lineClamp={1}>
                  {product.subtitle}
                </Text>
              ) : null}
            </Stack>
          );
        },
      },
      {
        field: "expectedPeriod",
        headerName: "Expected Period",
        sortable: true,
        cellRender: (col, bid) => (
          <Text size="xs">{formatExpectedPeriod(bid.expectedPeriod)}</Text>
        ),
      },
      {
        field: "expectedAmount",
        headerName: "Amount",
        sortable: true,
        cellRender: (col, bid) => {
          const amount = bid.expectedAmount ?? bid.paidAmount ?? 0;
          return (
            <Text
              size="xs"
              weight={500}
              align="right"
              color={amount >= 0 ? "green" : "red"}
            >
              {formatter.currency(amount)}
            </Text>
          );
        },
      },
    ],
    [formatter],
  );

  const handleSearchChange = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handlePeriodChange = (value) => {
    setPeriodFilter(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(Number(size) || PAGE_SIZE);
    setCurrentPage(1);
  };

  if (!form?.values) {
    return (
      <Group position="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!walletId) {
    return (
      <Text size="sm" color="dimmed">
        Save the wallet to view forecast bids.
      </Text>
    );
  }

  const totalPage = Math.max(1, Math.ceil(totalDocs / pageSize) || 1);

  return (
    <Stack spacing="md">
      <Group spacing="lg">
        <Text size="sm">
          <Text component="span" color="dimmed">
            Expected bids:{" "}
          </Text>
          <Text component="span" weight={600}>
            {totalDocs}
          </Text>
        </Text>
        <Text size="sm">
          <Text component="span" color="dimmed">
            Total amount:{" "}
          </Text>
          <Text component="span" weight={600}>
            {formatter.currency(totalAmount)}
          </Text>
        </Text>
        {loading && <Loader size="xs" />}
      </Group>

      <DataTable
        data={bids}
        columns={columns}
        hidePagination={false}
        hideSearchBar={false}
        searchText={searchText}
        onSearchChange={handleSearchChange}
        currentPage={currentPage}
        pageSize={pageSize}
        pagination={{
          total: totalDocs,
          page: currentPage,
          totalPage,
        }}
        onPageChange={setCurrentPage}
        onPageSizeChange={handlePageSizeChange}
        padding={0}
        searchBarExtra={
          <NativeSelect
            aria-label="YYYY-MM"
            placeholder="YYYY-MM"
            data={[{ value: "", label: "YYYY-MM" }, ...monthOptions]}
            value={periodFilter ?? ""}
            onChange={(event) =>
              handlePeriodChange(event.currentTarget.value || null)
            }
            size="sm"
            style={{ width: 140 }}
          />
        }
      />
    </Stack>
  );
};

export default WalletForecastBids;
